import showDetailProjectStyles from "@features/projects/styles/showdetailproject.module.scss";
import { useGetAllCommentsByTask } from "../../../../hooks/comments/useGetAllCommentsByTask";
import { IComment, IDocUploaded } from "@/api/comments/types";
import { getTimeFromXDate } from "@/utils/dateFunctions";
import { useGetAllDocsByComment } from "@/features/projects/hooks/comments/useGetAllDocsByComment";
import { useLoadingNotification } from "@/hooks/useLoadingNotification";
import { useDownloadDocFromComment } from "@/features/projects/hooks/comments/useDownloadDocFromComment";
import { toast } from "react-toastify";
import { useEffect, useState } from "react";
import { useUploadDocToComment } from "@/features/projects/hooks/comments/useUploadDocToComment";
import { FileUpload } from "@/components/UI";
import { Modal } from "@/components/UI";
import UIcon from "@/assets/uicons/UIcon";

export interface IComentsProjectProps {
  taskId: string;
}

interface PreviewModalState {
  isOpen: boolean;
  file: IDocUploaded | null;
  content: string | null;
  contentType: string | null;
}

export const TaskComments = ({ taskId }: IComentsProjectProps) => {
  const { comments, isLoading: isLoadingComments } = useGetAllCommentsByTask(taskId) as {
    comments: IComment[], isLoading: boolean, refetch: () => void
  };
  const { mutateAsync: downloadDoc, isPending: isPendingDownload } = useDownloadDocFromComment();
  const { uploadDoc, isPending: isUploadingDocument, isError } = useUploadDocToComment();
  const [selectedCommentId, setSelectedCommentId] = useState('');
  const [showAllComments, setShowAllComments] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState<{ isOpen: boolean; fileId: string | null }>({
    isOpen: false,
    fileId: null
  });
  const [previewModal, setPreviewModal] = useState<PreviewModalState>({
    isOpen: false,
    file: null,
    content: null,
    contentType: null
  });
  const commentIds = comments.map((comment: IComment) => comment.id);
  const { docs, refetch: refetchDocs } = useGetAllDocsByComment(commentIds);

  const getFileIcon = (fileName: string) => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') return 'file-pdf';
    if (ext === 'jpg' || ext === 'jpeg' || ext === 'png' || ext === 'gif' || ext === 'bmp' || ext === 'svg' || ext === 'webp') return 'file-image';
    if (ext === 'doc' || ext === 'docx') return 'file-text';
    if (ext === 'txt' || ext === 'rtf' || ext === 'md') return 'file-text';
    if (ext === 'zip' || ext === 'rar' || ext === '7z' || ext === 'tar' || ext === 'gz' || ext === 'bz2') return 'file-archive';
    if (ext === 'mp4' || ext === 'mov' || ext === 'avi' || ext === 'mkv' || ext === 'wmv' || ext === 'flv' || ext === 'webm') return 'file-video';
    if (ext === 'mp3' || ext === 'wav' || ext === 'flac' || ext === 'aac' || ext === 'ogg' || ext === 'wma') return 'file-audio';
    if (ext === 'xls' || ext === 'xlsx' || ext === 'csv') return 'file-spreadsheet';
    if (ext === 'ppt' || ext === 'pptx') return 'file-spreadsheet';
    if (ext === 'js' || ext === 'ts' || ext === 'jsx' || ext === 'tsx' || ext === 'html' || ext === 'css' || ext === 'scss' || ext === 'json' || ext === 'xml' || ext === 'php' || ext === 'py' || ext === 'java' || ext === 'cpp' || ext === 'c' || ext === 'cs' || ext === 'rb' || ext === 'go' || ext === 'rs' || ext === 'swift' || ext === 'kt') return 'file-code';
    if (ext === 'exe' || ext === 'msi' || ext === 'dmg' || ext === 'pkg' || ext === 'deb' || ext === 'rpm' || ext === 'apk') return 'file-generic';
    return 'file-generic';
  };

  const getSimplifiedFileType = (fileType: string) => {
    const typeMap: { [key: string]: string } = {
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PPTX',
      'application/vnd.ms-excel': 'XLS',
      'application/vnd.ms-powerpoint': 'PPT',
      'application/msword': 'DOC',
      'application/pdf': 'PDF',
      'image/jpeg': 'JPG',
      'image/png': 'PNG',
      'image/gif': 'GIF',
      'image/svg+xml': 'SVG',
      'image/webp': 'WEBP',
      'video/mp4': 'MP4',
      'video/avi': 'AVI',
      'video/mov': 'MOV',
      'video/webm': 'WEBM',
      'audio/mpeg': 'MP3',
      'audio/wav': 'WAV',
      'audio/ogg': 'OGG',
      'text/plain': 'TXT',
      'text/markdown': 'MD',
      'application/json': 'JSON',
      'application/xml': 'XML',
      'application/zip': 'ZIP',
      'application/x-rar-compressed': 'RAR',
      'text/html': 'HTML',
      'text/css': 'CSS',
      'application/javascript': 'JS',
      'application/typescript': 'TS',
      'application/x-php': 'PHP',
      'text/x-python': 'PY',
      'text/x-java-source': 'JAVA',
      'text/x-c++src': 'CPP',
      'text/x-csrc': 'C',
      'application/x-ruby': 'RB',
      'text/x-go': 'GO',
      'text/x-rust': 'RS',
      'text/x-swift': 'SWIFT',
      'text/x-kotlin': 'KT'
    };

    if (typeMap[fileType.toLowerCase()]) {
      return typeMap[fileType.toLowerCase()];
    }

    const mimeToExt: { [key: string]: string } = {
      'application/vnd.openxmlformats-officedocument': 'DOCX',
      'application/vnd.ms-': 'MS',
      'image/': 'IMG',
      'video/': 'VID',
      'audio/': 'AUD',
      'text/': 'TXT',
      'application/': 'APP'
    };

    for (const [prefix, ext] of Object.entries(mimeToExt)) {
      if (fileType.toLowerCase().startsWith(prefix)) {
        return ext;
      }
    }

    return 'FILE';
  };

  const isPreviewableFile = (fileType: string): boolean => {
    const previewableTypes = [
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/svg+xml',
      'image/webp',
      'image/bmp',
      'text/plain',
      'text/html',
      'text/css',
      'text/javascript',
      'application/javascript',
      'application/json',
      'application/xml',
      'video/mp4',
      'video/webm',
      'video/ogg',
      'audio/mpeg',
      'audio/wav',
      'audio/ogg'
    ];
    
    return previewableTypes.includes(fileType.toLowerCase());
  };

  const handleFilePreview = async (file: IDocUploaded) => {
    if (!isPreviewableFile(file.fileType)) {
      // Si no es previsualizable, descargar directamente
      await downLoad(file.id);
      return;
    }

    try {
      setPreviewModal({
        isOpen: true,
        file,
        content: null,
        contentType: null
      });

      const response = await downloadDoc(file.id);
      
      if (response && response.data) {
        const blob = new Blob([response.data], { type: file.fileType });
        const url = URL.createObjectURL(blob);
        
        setPreviewModal(prev => ({
          ...prev,
          content: url,
          contentType: file.fileType
        }));
      }
    } catch (error) {
      console.error('Error al previsualizar archivo:', error);
      toast.error('Error al previsualizar el archivo');
      setPreviewModal({
        isOpen: false,
        file: null,
        content: null,
        contentType: null
      });
    }
  };

  const closePreviewModal = () => {
    if (previewModal.content) {
      URL.revokeObjectURL(previewModal.content);
    }
    setPreviewModal({
      isOpen: false,
      file: null,
      content: null,
      contentType: null
    });
  };

  const toggleDropdown = (fileId: string) => {
    setDropdownOpen(prev => ({
      isOpen: !prev.isOpen || prev.fileId !== fileId,
      fileId: prev.fileId === fileId && prev.isOpen ? null : fileId
    }));
  };

  const closeDropdown = () => {
    setDropdownOpen({ isOpen: false, fileId: null });
  };

  const handleChipClick = (file: IDocUploaded, e: React.MouseEvent) => {
    // Prevenir que el clic se propague al dropdown
    e.stopPropagation();
    
    // Si es previsualizable, abrir vista previa
    if (isPreviewableFile(file.fileType)) {
      handleFilePreview(file);
    } else {
      // Si no es previsualizable, descargar directamente
      downLoad(file.id);
    }
  };

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownOpen.isOpen) {
        const target = event.target as Element;
        if (!target.closest('.file-chip-dropdown-btn') && !target.closest('.file-dropdown-menu')) {
          closeDropdown();
        }
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [dropdownOpen.isOpen]);

  const renderCommentHTML = (htmlText: string) => {
    return (
      <div 
        className={showDetailProjectStyles["comment-text"]}
        dangerouslySetInnerHTML={{ __html: htmlText }}
      />
    );
  };

  // Lógica para mostrar comentarios
  const totalComments = Array.isArray(comments) ? comments.length : 0;
  const visibleComments = showAllComments 
    ? comments 
    : Array.isArray(comments) 
      ? comments.slice(-4) 
      : [];
  const hiddenCommentsCount = totalComments - visibleComments.length;

  useLoadingNotification({ validator: isPendingDownload, message: "Descargando documento..." });
  useLoadingNotification({ validator: isUploadingDocument, message: "Subiendo adjunto..." });

  useEffect(() => {
    if (isLoadingComments) {
      toast.loading('Cargando comentarios...');
    } else {
      toast.dismiss();
    }
  }, [isLoadingComments]);

  useEffect(() => {
    if (isError) {
      toast.error('Error al subir el documento. Inténtalo nuevamente.');
    }
  }, [isError]);

  const downLoad = async (fileId: string) => {
    try {
      const response = await downloadDoc(fileId);

      if (!response?.data) {
        toast.error('No se recibieron datos del documento');
        throw new Error('No se recibieron datos del documento');
      }

      const contentType = response.headers['content-type'];
      const blob = new Blob([response.data], { type: contentType });
      const url = URL.createObjectURL(blob);

      if (contentType.startsWith('image/')) {
        const newWindow = window.open(url, '_blank');
        if (newWindow) {
          newWindow.addEventListener('load', () => {
            URL.revokeObjectURL(url);
          });
        }
      } else {
        const fileName = response.headers['content-disposition']
          ?.split('filename=')[1]
          ?.replace(/['"]/g, '')
          || `document-${fileId}`;

        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error downloading:', error);
      toast.error('Error al descargar el documento. Inténtalo nuevamente.');
    }
  };

  const showOptions = (commentId: string) => {
    setSelectedCommentId(commentId);
  };

  const uploadFiles = async (files: File[], commentId: string) => {
    await Promise.all(
      files.map(async (file) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("commentId", commentId);

        try {
          const response = await uploadDoc(formData);
          if (response?.success) {
            toast.success("Adjunto subido correctamente");
          }
        } catch (error) {
          console.error("Error al subir el documento:", error);
        }
      })
    );
    refetchDocs();
    setSelectedCommentId('');
  };

  return (
    <div className={showDetailProjectStyles["comments-container"]}>
      {/* CTA para mostrar comentarios ocultos */}
      {!showAllComments && hiddenCommentsCount > 0 && (
        <div className={showDetailProjectStyles["comments-expand-cta"]}>
          <button 
            onClick={() => setShowAllComments(true)}
            className={showDetailProjectStyles["comments-expand-button"]}
          >
            {hiddenCommentsCount} comentario{hiddenCommentsCount !== 1 ? 's' : ''} anterior{hiddenCommentsCount !== 1 ? 'es' : ''}
          </button>
        </div>
      )}

      {Array.isArray(visibleComments) && visibleComments.map((comment: IComment) => (
        <div className={showDetailProjectStyles["comment-item"]} key={comment.id}>
          <div className={showDetailProjectStyles["comment-avatar"]}>
            <img 
              src={`https://ui-avatars.com/api/?name=${comment.author}&background=random&size=32`} 
              alt={comment.author}
              className={showDetailProjectStyles["comment-avatar-img"]}
            />
          </div>
          
          <div className={showDetailProjectStyles["comment-content"]}>
            <div className={showDetailProjectStyles["comment-header"]}>
              <div className={showDetailProjectStyles["comment-meta"]}>
                <span className={showDetailProjectStyles["comment-author"]}>{comment.author}</span>
                <span className={showDetailProjectStyles["comment-time"]}>
                  {getTimeFromXDate(comment.createdAt).timeSince}
                </span>
              </div>
              <button 
                className={showDetailProjectStyles["comment-menu-button"]} 
                onClick={() => showOptions(comment.id)}
                title="Más opciones"
              >
                ⋯
              </button>
            </div>
            
            <div className={showDetailProjectStyles["comment-body"]}>
              {renderCommentHTML(comment.text)}
              
              {docs && docs(comment.id).length > 0 && (
                <div className={showDetailProjectStyles["files-simple-container"]}>
                  {docs(comment.id).map((doc: IDocUploaded) => (
                    <div 
                      key={doc.id} 
                      className={showDetailProjectStyles["file-chip"]}
                      onClick={(e) => handleChipClick(doc, e)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className={showDetailProjectStyles["file-chip-icon"]}>
                        <UIcon name={getFileIcon(doc.fileName)} size={16} />
                      </div>
                      <div className={showDetailProjectStyles["file-chip-content"]}>
                        <div className={showDetailProjectStyles["file-chip-name"]} title={doc.fileName}>
                          {doc.fileName}
                        </div>
                        <div className={showDetailProjectStyles["file-chip-meta"]}>
                          {getSimplifiedFileType(doc.fileType)} • Descargar
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation(); // Evitar que se active el onClick del chip
                          toggleDropdown(doc.id);
                        }}
                        className={showDetailProjectStyles["file-chip-dropdown-btn"]}
                        title="Más opciones"
                      >
                        <UIcon name="chevrons-up-down" size={12} />
                      </button>
                      
                      {/* Dropdown menu */}
                      {dropdownOpen.isOpen && dropdownOpen.fileId === doc.id && (
                        <div className={showDetailProjectStyles["file-dropdown-menu"]}>
                          <button
                            type="button"
                            onClick={() => {
                              handleFilePreview(doc);
                              closeDropdown();
                            }}
                            className={showDetailProjectStyles["file-dropdown-item"]}
                          >
                            <UIcon name="eye" size={14} />
                            Vista previa
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              downLoad(doc.id);
                              closeDropdown();
                            }}
                            className={showDetailProjectStyles["file-dropdown-item"]}
                          >
                            <UIcon name="download" size={14} />
                            Descargar
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          {selectedCommentId === comment.id && (
            <Modal isOpen={true} onClose={() => setSelectedCommentId('')}>
              <div className={showDetailProjectStyles["upload-modal"]}>
                <h3>Subir archivo al comentario</h3>
                <FileUpload
                  onFilesSelected={(files) => uploadFiles(files, selectedCommentId)}
                  maxFiles={3}
                  showPreview={false}
                  disabled={isUploadingDocument}
                />
                <div className={showDetailProjectStyles["upload-modal-actions"]}>
                  <button 
                    onClick={() => setSelectedCommentId('')}
                    className={showDetailProjectStyles["upload-modal-cancel"]}
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </Modal>
          )}
        </div>
      ))}

      {/* Modal de Previsualización Interno */}
      {previewModal.isOpen && previewModal.content && previewModal.file && (
        <div className={showDetailProjectStyles["preview-modal-overlay"]}>
          <div className={showDetailProjectStyles["preview-modal-content"]}>
            {/* Header del modal */}
            <div className={showDetailProjectStyles["preview-modal-header"]}>
              <div className={showDetailProjectStyles["preview-modal-file-info"]}>
                <UIcon 
                  name={getFileIcon(previewModal.file.fileName)} 
                  size={24} 
                />
                <div className={showDetailProjectStyles["preview-modal-file-details"]}>
                  <span className={showDetailProjectStyles["preview-modal-file-name"]}>
                    {previewModal.file.fileName}
                  </span>
                  <span className={showDetailProjectStyles["preview-modal-file-meta"]}>
                    {getSimplifiedFileType(previewModal.contentType || '')} • Archivo guardado
                  </span>
                </div>
              </div>
              <div className={showDetailProjectStyles["preview-modal-actions"]}>
                <button
                  onClick={() => downLoad(previewModal.file!.id)}
                  className={showDetailProjectStyles["preview-modal-btn"]}
                  title="Descargar"
                >
                  <UIcon name="download" size={16} />
                </button>
                <button
                  onClick={closePreviewModal}
                  className={showDetailProjectStyles["preview-modal-btn"]}
                  title="Cerrar"
                >
                  <UIcon name="cross" size={16} />
                </button>
              </div>
            </div>

            {/* Contenido del archivo */}
            <div className={showDetailProjectStyles["preview-modal-body"]}>
              {previewModal.contentType?.startsWith('image/') ? (
                <img 
                  src={previewModal.content} 
                  alt={previewModal.file.fileName}
                  className={showDetailProjectStyles["preview-modal-image"]}
                />
              ) : previewModal.contentType?.startsWith('video/') ? (
                <video 
                  src={previewModal.content} 
                  controls
                  className={showDetailProjectStyles["preview-modal-video"]}
                >
                  Tu navegador no soporta el elemento de video.
                </video>
              ) : previewModal.contentType?.startsWith('audio/') ? (
                <audio 
                  src={previewModal.content} 
                  controls
                  className={showDetailProjectStyles["preview-modal-audio"]}
                >
                  Tu navegador no soporta el elemento de audio.
                </audio>
              ) : previewModal.contentType === 'application/pdf' ? (
                <iframe 
                  src={previewModal.content} 
                  className={showDetailProjectStyles["preview-modal-pdf"]}
                  title={previewModal.file.fileName}
                />
              ) : (
                <div className={showDetailProjectStyles["preview-modal-unsupported"]}>
                  <UIcon name="file-generic" size={64} />
                  <p>Vista previa no disponible para este tipo de archivo</p>
                  <button
                    onClick={() => downLoad(previewModal.file!.id)}
                    className={showDetailProjectStyles["preview-modal-download-btn"]}
                  >
                    <UIcon name="download" size={16} />
                    Descargar archivo
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
